import authenticatedAxiosInstance from './authenticatedAxiosInstance.service';
import authHeader from './auth-header';

const API_URL = process.env.REACT_APP_API_URL + '/bug-reports';

export interface BugReportSubmission {
  title?: string;
  description: string;
  url_at_submission?: string;
  user_agent?: string;
  app_version?: string;
  viewport_size?: string;
  app_insights_operation_id?: string;
  /** PNG/JPEG blob produced by html2canvas (optional). */
  screenshot?: Blob | null;
}

export interface BugReportListItem {
  id: number;
  user_id: number;
  company_id: number | null;
  title: string | null;
  description: string;
  screenshot_blob_path: string | null;
  screenshot_upload_failed: boolean;
  url_at_submission: string | null;
  user_agent: string | null;
  app_version: string | null;
  viewport_size: string | null;
  app_insights_operation_id: string | null;
  severity: 'low' | 'medium' | 'high' | 'critical' | null;
  status: 'new' | 'triaged' | 'in_progress' | 'resolved' | 'wontfix' | 'duplicate';
  resolution_notes: string | null;
  resolved_by: number | null;
  resolved_at: string | null;
  created_at: string;
  updated_at: string;
  submitter?: {
    id: number;
    username: string | null;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    company_id: number | null;
  } | null;
}

class BugReportsService {
  /** User-side: submit a new bug report (multipart). */
  async submit(payload: BugReportSubmission) {
    const form = new FormData();
    if (payload.title) form.append('title', payload.title);
    form.append('description', payload.description);
    if (payload.url_at_submission) form.append('url_at_submission', payload.url_at_submission);
    if (payload.user_agent) form.append('user_agent', payload.user_agent);
    if (payload.app_version) form.append('app_version', payload.app_version);
    if (payload.viewport_size) form.append('viewport_size', payload.viewport_size);
    if (payload.app_insights_operation_id) {
      form.append('app_insights_operation_id', payload.app_insights_operation_id);
    }
    if (payload.screenshot) {
      form.append('screenshot', payload.screenshot, 'screenshot.png');
    }
    return authenticatedAxiosInstance.post<{
      message: string;
      id: number;
      screenshot_upload_failed: boolean;
    }>(API_URL, form, {
      headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' },
    });
  }

  /** Admin: list reports with optional filters and pagination. */
  list(params?: {
    status?: string;
    severity?: string;
    user_id?: number;
    limit?: number;
    offset?: number;
  }) {
    return authenticatedAxiosInstance.get<{ items: BugReportListItem[]; total: number }>(API_URL, {
      headers: authHeader(),
      params,
    });
  }

  /** Admin: fetch one report. */
  getById(id: number) {
    return authenticatedAxiosInstance.get<BugReportListItem>(`${API_URL}/${id}`, {
      headers: authHeader(),
    });
  }

  /** Admin: triage update. */
  update(
    id: number,
    update: Partial<Pick<BugReportListItem, 'status' | 'severity' | 'resolution_notes'>>
  ) {
    return authenticatedAxiosInstance.patch(`${API_URL}/${id}`, update, {
      headers: authHeader(),
    });
  }
}

export default new BugReportsService();
